if (!customElements.get('rs-product-editorial-tabs')) {
  class RSProductEditorialTabs extends HTMLElement {
    connectedCallback() {
      this._controller?.abort();
      this._controller = new AbortController();
      this.tabs = [...this.querySelectorAll('[data-pet-tab]')];
      this.panels = [...this.querySelectorAll('[data-pet-panel]')];
      this.tablist = this.querySelector('[data-pet-tablist]');
      this._animations = [];
      this._transition = 0;
      this._reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._mobile = window.matchMedia('(max-width: 749px)');
      if (!this.tabs.length || !this.tablist) return;
      this.activeIndex = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
      this._syncOrientation();
      this._settle(this.activeIndex);
      this._safeColorAnchor = this.dataset.colorAnchor !== 'false';
      this._observeMainGallery();
      this._syncAutoImages();
      const signal = this._controller.signal;
      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-pet-tab]');
        if (tab && this.tabs.includes(tab)) this.activate(this.tabs.indexOf(tab));
      }, { signal });
      this.addEventListener('keydown', (event) => this._onKeydown(event), { signal });
      document.addEventListener('variant:update', (event) => {
        if (String(event.detail?.data?.productId || '') !== this.dataset.productId) return;
        if (event.target instanceof Element && event.target.closest('dialog, fablair-commerce-quick, quick-add-component')) return;
        const resource = event.detail?.resource;
        this._safeColorAnchor = this.dataset.multipleColors !== 'true' || Boolean(resource?.featured_media || resource?.featured_image);
        this._queueImageSync();
      }, { signal });
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.tabs.findIndex((tab) => tab.dataset.blockId === event.detail?.blockId);
        if (index >= 0) this.activate(index, { animate: false });
      }, { signal });
      this._onBreakpoint = () => this._syncOrientation();
      this._onMotion = () => { if (this._reducedMotion.matches) this._settle(this.activeIndex); };
      this._mobile.addEventListener?.('change', this._onBreakpoint);
      this._reducedMotion.addEventListener?.('change', this._onMotion);
    }

    disconnectedCallback() {
      this._controller?.abort();
      this._galleryObserver?.disconnect();
      this._observedProduct = null;
      this._mobile?.removeEventListener?.('change', this._onBreakpoint);
      this._reducedMotion?.removeEventListener?.('change', this._onMotion);
      this._cancelAnimations();
    }

    _findMainProduct() {
      const productId = this.dataset.productId;
      if (!productId) return null;
      return [...document.querySelectorAll('.product-information')].find((node) => {
        const wrapper = node.closest('rs-product-editorial');
        return wrapper?.dataset.productId === productId || node.querySelector('variant-picker')?.dataset.productId === productId;
      }) || null;
    }

    _observeMainGallery() {
      const mainProduct = this._findMainProduct();
      if (mainProduct === this._observedProduct) return;
      this._galleryObserver?.disconnect();
      this._observedProduct = mainProduct;
      if (!mainProduct) return;
      this._galleryObserver = new MutationObserver(() => this._queueImageSync());
      this._galleryObserver.observe(mainProduct, { childList: true, subtree: true });
    }

    _queueImageSync() {
      if (this._imageSyncQueued) return;
      this._imageSyncQueued = true;
      queueMicrotask(() => {
        this._imageSyncQueued = false;
        if (!this.isConnected) return;
        this._observeMainGallery();
        this._syncAutoImages();
      });
    }

    _syncAutoImages() {
      const automatic = [...this.querySelectorAll('[data-pet-auto-image]')];
      if (!automatic.length) return;
      const gallery = this._findMainProduct()?.querySelector('media-gallery');
      // Grid items are already restricted to the selected color by the native Liquid gallery.
      // Zoom and mobile slideshow copies are deliberately excluded.
      const images = gallery ? [...gallery.querySelectorAll(':scope > .media-gallery__grid > .product-media-container .product-media__image')]
        .filter((image) => !image.closest('[hidden]') && image.getAttribute('src')) : [];
      const safeColorGroup = gallery?.dataset.colorMediaFiltered === 'true' ? gallery.dataset.colorMediaEmpty !== 'true' : this._safeColorAnchor;
      automatic.forEach((holder) => {
        const panel = holder.closest('[data-pet-panel]');
        const image = holder.querySelector('img');
        const source = this.dataset.multipleColors === 'true' && gallery?.dataset.colorMediaFiltered === 'false' ? images[0] : images[Number(holder.dataset.imageIndex)] || images[0];
        if (!safeColorGroup || (gallery && !source)) {
          holder.hidden = true;
          image.hidden = true;
          panel.classList.add('rs-pet__panel--text-only');
          return;
        }
        if (!source) return; // Retain the safe server-rendered variant image if no main gallery is present.
        image.setAttribute('src', source.getAttribute('src'));
        const srcset = source.getAttribute('srcset');
        if (srcset) image.setAttribute('srcset', srcset);
        else image.removeAttribute('srcset');
        image.setAttribute('alt', source.getAttribute('alt') || '');
        image.setAttribute('sizes', '(min-width: 990px) 36vw, (min-width: 750px) 40vw, 100vw');
        image.hidden = false;
        holder.hidden = false;
        panel.classList.remove('rs-pet__panel--text-only');
      });
    }

    _syncOrientation() {
      this.tablist.setAttribute('aria-orientation', this._mobile.matches ? 'horizontal' : 'vertical');
    }

    _cancelAnimations() {
      this._transition = (this._transition || 0) + 1;
      this._animations?.forEach((animation) => animation.cancel());
      this._animations = [];
    }

    _settle(index) {
      this._cancelAnimations();
      this.panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.hidden = !active;
        panel.inert = !active;
        if (active) panel.removeAttribute('aria-hidden');
        else panel.setAttribute('aria-hidden', 'true');
      });
    }

    _onKeydown(event) {
      const tab = event.target.closest('[data-pet-tab]');
      const index = this.tabs.indexOf(tab);
      if (index < 0 || event.altKey || event.ctrlKey || event.metaKey) return;
      const horizontal = this._mobile.matches;
      const rtl = horizontal && getComputedStyle(this.tablist).direction === 'rtl';
      let next;
      if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.tabs.length - 1;
      else if ((!horizontal && event.key === 'ArrowDown') || (horizontal && event.key === (rtl ? 'ArrowLeft' : 'ArrowRight'))) next = (index + 1) % this.tabs.length;
      else if ((!horizontal && event.key === 'ArrowUp') || (horizontal && event.key === (rtl ? 'ArrowRight' : 'ArrowLeft'))) next = (index - 1 + this.tabs.length) % this.tabs.length;
      else return;
      event.preventDefault();
      this.activate(next, { focus: true });
    }

    activate(index, { focus = false, animate = true } = {}) {
      if (index < 0 || index >= this.tabs.length) return;
      const previous = this.activeIndex;
      if (index === previous) {
        if (focus) this.tabs[index].focus({ preventScroll: true });
        return;
      }
      this._settle(previous);
      const oldPanel = this.panels[previous];
      const nextPanel = this.panels[index];
      // A programmatic editor selection must not leave focus inside an inactive panel.
      if (oldPanel?.contains(document.activeElement)) focus = true;
      this.activeIndex = index;
      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.inert = !active;
        if (active) panel.removeAttribute('aria-hidden');
        else panel.setAttribute('aria-hidden', 'true');
      });
      nextPanel.hidden = false;
      if (focus) this.tabs[index].focus({ preventScroll: true });
      if (this._mobile.matches) this._revealTab(this.tabs[index]);
      const canAnimate = animate && !this._reducedMotion.matches && typeof nextPanel.animate === 'function';
      if (!canAnimate) {
        this._settle(index);
        return;
      }
      // The outgoing panel is inert and hidden from assistive technology during the crossfade.
      // Both panels occupy one grid cell, avoiding a temporary second row.
      const transition = this._transition;
      const options = { duration: 220, easing: 'ease-out', fill: 'both' };
      this._animations = [
        oldPanel.animate([{ opacity: 1 }, { opacity: 0 }], options),
        nextPanel.animate([{ opacity: 0 }, { opacity: 1 }], options),
      ];
      Promise.allSettled(this._animations.map((animation) => animation.finished)).then(() => {
        if (transition === this._transition && this.isConnected) this._settle(index);
      });
    }

    _revealTab(tab) {
      const box = tab.getBoundingClientRect();
      const viewport = this.tablist.getBoundingClientRect();
      const delta = box.left < viewport.left ? box.left - viewport.left : box.right > viewport.right ? box.right - viewport.right : 0;
      if (delta) this.tablist.scrollBy?.({ left: delta, behavior: this._reducedMotion.matches ? 'instant' : 'smooth' });
    }
  }
  customElements.define('rs-product-editorial-tabs', RSProductEditorialTabs);
}
